import { Component, input, ViewChild, ElementRef, AfterViewInit, computed, signal } from '@angular/core';
import { barChartColorsDarkModeMap, barChartColorsMap, BarColor, EBarChartFontColor, ICompositionChartOptions } from 'src/models/charts.model';
import { ILabelValuePair } from 'src/models/common.model';

@Component({
  selector: 'liberro-investment-composition-chart',
  standalone: false,
  templateUrl: './liberro-investment-composition-chart.component.html',
  styleUrl: './liberro-investment-composition-chart.component.less'
})
export class LiberroInvestmentCompositionChartComponent implements AfterViewInit {
  @ViewChild('chartContainer', { static: false }) private chartContainer?: ElementRef;

  public chartData = input.required<ILabelValuePair[]>();
  public darkMode = input.required<boolean>();
  private narrowBarThresholdBase = 15;
  private barHeight = 72;
  private baseRowHeightPadding = 9;
  private singleRowHeightMultiplier = 1.3;
  private narrowBarLongLabelThreshold = 8;
  private desktopThresholdWidth = 706;
  private svgNamespace = 'http://www.w3.org/2000/svg';
  public chartOptions = computed<ICompositionChartOptions | null>(() => this.chartData().length ? this.getChartData() : null);
  private chartWidth = signal<number | null>(null);
  private chartBarColors = computed<Map<BarColor, EBarChartFontColor>>(() => this.darkMode() ? barChartColorsDarkModeMap : barChartColorsMap);

  public ngAfterViewInit(): void {
    this.addCategoryLabels()
  }

  private addCategoryLabels(): void {
    if (!this.chartContainer) return;

    const data = this.chartData();
    if (!data || data.length === 0) return;

    const chartElement = this.chartContainer.nativeElement;
    const svg = chartElement.querySelector('svg');
    if (!svg) return;

    const svgChartWidth = chartElement.offsetWidth || svg.getBoundingClientRect().width || 0;
    if (svgChartWidth > 0 && this.chartWidth() !== svgChartWidth) {
      this.chartWidth.set(svgChartWidth);
    }

    const dataLabels = svg.querySelectorAll('.apexcharts-datalabel');
    const bars: SVGRectElement[] = svg.querySelectorAll('.apexcharts-bar-area');

    // For data length of 1, Apex is keeping the bar area height fixed to 36px, we need to adjust it
    // so that it matches multi rows heights
    const isSingleRow = data.length === 1;

    dataLabels.forEach((label: SVGTextElement, index: number) =>
      this.processDataLabels(data, bars, label, index, isSingleRow)
    );
  }

  private processDataLabels(
    data: ILabelValuePair[],
    bars: SVGRectElement[],
    label: SVGTextElement,
    index: number,
    isSingleRow: boolean
  ): void {
    if (index >= data.length) return;

    const item = data[index];

    const existingLabel = label.querySelector('tspan:nth-child(2)');
    if (existingLabel) return;

    const narrowBarThreshold = this.getNarrowBarThreshold(item);
    const isNarrow = item.value < narrowBarThreshold;
    const narrowDoubleDigits = isNarrow && item.value >= 10;
    const barWidthAtrr = bars[index].getAttribute('barWidth');
    bars[index].setAttribute('barHeight', this.barHeight.toString());
    bars[index].setAttribute('cy', this.barHeight.toString());

    let barWidthNum: number = barWidthAtrr ? +barWidthAtrr : 15;

    if (isNarrow) {
      barWidthNum += narrowDoubleDigits ? 70 : 60;
    } else {
      barWidthNum -= 16;
    }

    this.resetBarsLabels(item, label, barWidthNum, isNarrow, isSingleRow);
  }

  private resetBarsLabels(
    item: ILabelValuePair,
    label: SVGTextElement,
    barWidthNum: number,
    isNarrow: boolean,
    isSingleRow: boolean
  ): void {
    const longLabelAdjustmentCharsCount = item.label.length - this.narrowBarLongLabelThreshold;
    // add 5px to each 9+ character when narrow
    const longLabelAdjustment = longLabelAdjustmentCharsCount > 0 && isNarrow ? (longLabelAdjustmentCharsCount * 5) : 0;
    const barWidth = (barWidthNum + longLabelAdjustment) + 'px';
    const labelTspan = document.createElementNS(this.svgNamespace, 'tspan');
    let percentageTspan = label.querySelector('tspan:first-child');
    if (!percentageTspan) {
      const textContent = label.textContent || '';
      if (textContent.trim()) {
        percentageTspan = document.createElementNS(this.svgNamespace, 'tspan');
        percentageTspan.setAttribute('x', barWidth);
        percentageTspan.setAttribute('dy', isSingleRow ? '-6' : '-8');
        percentageTspan.textContent = textContent.trim();
        label.textContent = '';
        label.appendChild(percentageTspan);
      } else {
        return;
      }
    }

    labelTspan.setAttribute('x', barWidth);
    labelTspan.setAttribute('dy', '16');
    labelTspan.textContent = item.label;
    label.appendChild(labelTspan);
  }

  /**
   * For any future changes, test manually for all regional labels with values:
   *
   * 1 - smalles possible
   * 14/15 (29/30 mobile) - cutoff to be placed inside bar for non-largest labels
   * 24/25 (49/50 mobile) - cutoff to be placed inside bar for largest labels
   * 26-100 - largest possible
   */
  private getNarrowBarThreshold(item: ILabelValuePair): number {
    let narrowBarThreshold = this.narrowBarThresholdBase;
    const isLongLabel = item.label.length > 12;
    // Adjustment for `Střední a východní Everopa` and `Rozvíjející se trhy`
    if (isLongLabel) {
      narrowBarThreshold += 10;
    }

    // Further adjustment if on mobile
    if (this.chartWidth() && this.chartWidth()! < this.desktopThresholdWidth) {
      narrowBarThreshold += 15;
      if (isLongLabel) {
        narrowBarThreshold += 10;
      }
    }

    return narrowBarThreshold;
  }

  private getChartData(): ICompositionChartOptions | null {
    const data = this.chartData();
    if (!data || data.length === 0) {
      return null;
    }

    const allColors: BarColor[] = Array.from(this.chartBarColors().keys());
    const dataColors: EBarChartFontColor[] = [];

    const series = [{
      name: 'Percentage',
      data: data.map((item, index) => {
        const narrowBarThreshold = this.getNarrowBarThreshold(item);
        if (item.value < narrowBarThreshold) {
          dataColors.push(this.darkMode() ? EBarChartFontColor.DM_LIGHT : EBarChartFontColor.DARK);
        } else {
          dataColors.push([...this.chartBarColors().values()][index]);
        }
        return ({
          x: item.label,
          y: item.value,
          isNarrow: item.value < narrowBarThreshold
        });
      })
    }];

    const baseRowHeight = this.barHeight + this.baseRowHeightPadding;
    const chartHeight =
      data.length === 1
        ? Math.round(baseRowHeight * this.singleRowHeightMultiplier)
        : data.length * baseRowHeight;

    return {
      series,
      chart: {
        type: 'bar',
        toolbar: {
          show: false
        },
        sparkline: {
          enabled: false
        },
        offsetX: 0,
        offsetY: 0,
        width: '100%',
        parentHeightOffset: 0,
        height: chartHeight,
        events: {
          mounted: () => this.addCategoryLabels(),
          updated: () => this.addCategoryLabels()
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '100%',
          dataLabels: {
            position: 'right',
            hideOverflowingLabels: false
          },
          distributed: true // Each bar gets its own color
        }
      },
      dataLabels: {
        enabled: true,
        textAnchor: 'end',
        style: {
          fontSize: '24px',
          fontWeight: 500,
          fontFamily: 'Plus Jakarta Sans',
          colors: dataColors,
        },
        formatter: (val: number) => {
          return `${val} %`;
        },
        dropShadow: {
          enabled: false
        }
      },
      xaxis: {
        labels: {
          show: false
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        },
        max: 100
      },
      yaxis: {
        labels: {
          show: false
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      grid: {
        show: false,
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0
        }
      },
      tooltip: {
        enabled: false
      },
      legend: {
        show: false
      },
      colors: allColors,
      states: {
        hover: {
         filter: {
            type: 'none'
          }
        }
      },
      fill: {
        opacity: 1
      }
    };
  }
}

